/**
 * Product Card Image Slider
 * Handles multi-image carousel functionality for product cards
 * Based on OLAY DSL - Madonna Library 3.0 Figma specs
 */

(function() {
  'use strict';

  class ProductCardSlider {
    constructor(card) {
      this.card = card;
      this.imageSlider = card.querySelector('.product-card--image-slider');
      this.images = card.querySelectorAll('.product-card--image, .product-card--slider-image, .product-card--hover-image');
      this.dots = card.querySelectorAll('.slider-dot');
      this.currentIndex = 0;
      this.autoPlayInterval = null;
      
      if (this.images.length > 1 && this.dots.length > 0) {
        this.init();
      }
    }

    init() {
      // Set up dot click and hover handlers
      this.dots.forEach((dot, index) => {
        // Click handler - locks in the selection
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToSlide(index);
        });
        
        // Hover handler - previews the image
        dot.addEventListener('mouseenter', () => {
          this.goToSlide(index);
        });
      });
      // Set up swipe handlers for mobile
      this.setupSwipeHandlers();

      // Set up keyboard navigation
      this.setupKeyboardNavigation();

      // Set up auto-play
      this.setupAutoPlay();

      // Set initial state
      this.updateSlide(0);
    }

    setupSwipeHandlers() {
      let touchStartX = 0;
      let touchEndX = 0;

      this.imageSlider.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.imageSlider.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    }

    handleSwipe(startX, endX) {
      const swipeThreshold = 50;
      const diff = startX - endX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next slide
          this.nextSlide();
        } else {
          // Swipe right - previous slide
          this.previousSlide();
        }
      }
    }

    setupKeyboardNavigation() {
      this.card.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.previousSlide();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextSlide();
        }
      });
    }

    setupAutoPlay() {
      // Start auto-play
      this.startAutoPlay();

      // Pause on hover (desktop)
      this.card.addEventListener('mouseenter', () => {
        this.stopAutoPlay();
      });

      this.card.addEventListener('mouseleave', () => {
        this.startAutoPlay();
      });

      // Pause when user interacts
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          this.stopAutoPlay();
          // Restart after 5 seconds of no interaction
          setTimeout(() => this.startAutoPlay(), 5000);
        });
      });
    }

    startAutoPlay() {
      if (this.images.length <= 1) return;
      
      this.stopAutoPlay();
    }

    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    goToSlide(index) {
      this.currentIndex = index;
      this.updateSlide(index);
    }

    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.updateSlide(this.currentIndex);
    }

    previousSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.updateSlide(this.currentIndex);
    }

    updateSlide(index) {
      // Update images
      this.images.forEach((img, i) => {
        if (i === index) {
          img.classList.add('active');
          img.setAttribute('aria-hidden', 'false');
        } else {
          img.classList.remove('active');
          img.setAttribute('aria-hidden', 'true');
        }
      });

      // Update dots
      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.add('active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('active');
          dot.removeAttribute('aria-current');
        }
      });
    }

    destroy() {
      this.stopAutoPlay();
    }
  }

  // Initialize all product card sliders
  function initProductCardSliders() {
    const productCards = document.querySelectorAll('.product-card--root');
    const sliders = [];

    productCards.forEach(card => {
      const slider = new ProductCardSlider(card);
      sliders.push(slider);
    });

    return sliders;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardSliders);
  } else {
    initProductCardSliders();
  }

  // Re-initialize when sections are loaded (for Shopify theme editor)
  document.addEventListener('shopify:section:load', initProductCardSliders);

  // Clean up when sections are unloaded
  document.addEventListener('shopify:section:unload', function(event) {
    const cards = event.target.querySelectorAll('.product-card--root');
    cards.forEach(card => {
      if (card._slider) {
        card._slider.destroy();
      }
    });
  });

  // Export for potential external use
  window.ProductCardSlider = ProductCardSlider;
})();
